import React, { useCallback, useState, ReactNode } from 'react';

interface DraggingAreaProps {
  children: ReactNode;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: () => void;
  className?: string;
  draggingClassName?: string;
  canUpload?: boolean;
  disabled?: boolean;
  layoutType?: 'grid' | 'table' | 'default';
  forceRerenderKey?: string | number;
}

export default function DraggingArea({
  children,
  onDrop: customOnDrop,
  onDragOver,
  onDragLeave,
  className = '',
  draggingClassName,
  canUpload = true,
  disabled = false,
  layoutType = 'default',
  forceRerenderKey
}: DraggingAreaProps) {
  const [isDragging, setIsDragging] = useState(false);

  // Default dragging styles based on layout type
  const getDefaultDraggingClassName = () => {
    switch (layoutType) {
      case 'grid':
        return 'border-1.5 border-dashed border-success h-full bg-[#2a835f48] rounded-lg';
      case 'table':
        return 'border-1.5 border-dashed border-success bg-[#2a835f48] rounded-lg';
      default:
        return 'border-1.5 border-dashed border-success h-full bg-[#2a835f48] rounded-lg';
    }
  };

  const finalDraggingClassName = draggingClassName || getDefaultDraggingClassName();

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!disabled && customOnDrop) {
      customOnDrop(event);
    }
  }, [customOnDrop, disabled]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (canUpload && !disabled) {
      setIsDragging(true);
      onDragOver?.(event);
    }
  }, [canUpload, disabled, onDragOver]);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
    onDragLeave?.();
  }, [onDragLeave]);

  // Create a key that includes the forceRerenderKey if provided
  const componentKey = forceRerenderKey ? `${forceRerenderKey}-${isDragging}` : undefined;

  return (
    <div
      key={componentKey}
      className={`w-full p-4 ${isDragging ? finalDraggingClassName : ''} ${className}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      style={{ zIndex: '1000' }}
    >
      {children}
    </div>
  );
}
